import z from 'zod';
// Imported for the side effect: it is where zod gains `.openapi`.
import './schemas';
import { createTransformer } from './utils';

/**
 * The Postgres types whose driver representation is not the shape an API caller
 * wants, with a transformer for each. Every SQL-backed codec composes these, so
 * a column's client shape cannot drift between one entity and the next.
 */

/** `bytea` as the driver returns it, checked as the `Uint8Array` a `Buffer` is. */
export const DBSha256Schema = z.custom<Buffer>(
  value => value instanceof Uint8Array && value.length === 32,
  'A SHA-256 digest is 32 bytes'
);

/** The same digest as an API caller writes and reads it. */
export const Sha256Schema = z
  .string()
  .regex(/^[0-9a-f]{64}$/, 'Must be a lowercase hex SHA-256 digest')
  .openapi({
    description: 'SHA-256 digest, lowercase hex',
    example: 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
  });

/** `timestamptz` as the driver returns it. */
export const DBTimestampSchema = z.date();

/** `bigint`, which the driver returns as a string so nothing is silently lost. */
export const DBBigIntSchema = z.union([z.string(), z.number(), z.bigint()]);

/** `numeric`, likewise. */
export const DBNumericSchema = z.union([z.string(), z.number()]);

/** A fraction of a page's width or height, so a crop survives a re-render. */
export const PageFractionSchema = z.number().min(0).max(1).openapi({
  description: 'Position as a fraction of the page, 0 to 1',
  example: 0.32,
});

/** `jsonb` holding an object the schema deliberately does not describe. */
export const DBJsonObjectSchema = z.record(z.string(), z.unknown());
export const JsonObjectSchema = z.record(z.string(), z.unknown());

type AnyObject = z.ZodObject<any>;

/**
 * A `bytea` digest, hex to a caller and bytes to the driver. A nullable column
 * passes `nullable`, so an absent digest stays absent.
 */
export const createSha256Transformer = <TClient extends AnyObject, TDB extends AnyObject>(
  field: string,
  { nullable = false }: { nullable?: boolean } = {}
) =>
  createTransformer<TClient, TDB>({
    encoder: {
      sourceFields: [field] as never,
      encode: value => {
        const hex = (value as Record<string, string | null>)[field];
        if (nullable && (hex === null || hex === undefined)) {
          return { [field]: null } as never;
        }
        return { [field]: Buffer.from(hex as string, 'hex') } as never;
      },
    },
    decoder: {
      sourceFields: [field] as never,
      decode: value => {
        const bytes = (value as Record<string, Buffer | null>)[field];
        if (nullable && (bytes === null || bytes === undefined)) {
          return { [field]: null } as never;
        }
        return { [field]: Buffer.from(bytes as Buffer).toString('hex') } as never;
      },
    },
  });

/** A `timestamptz`, unix milliseconds to a caller and a `Date` to the driver. */
export const createTimestampTransformer = <TClient extends AnyObject, TDB extends AnyObject>(
  field: string,
  { nullable = false }: { nullable?: boolean } = {}
) =>
  createTransformer<TClient, TDB>({
    encoder: {
      sourceFields: [field] as never,
      encode: value => {
        const millis = (value as Record<string, number | null>)[field];
        if (nullable && (millis === null || millis === undefined)) {
          return { [field]: null } as never;
        }
        return { [field]: new Date(millis as number) } as never;
      },
    },
    decoder: {
      sourceFields: [field] as never,
      decode: value => {
        const at = (value as Record<string, Date | null>)[field];
        if (nullable && (at === null || at === undefined)) {
          return { [field]: null } as never;
        }
        return { [field]: new Date(at as Date).getTime() } as never;
      },
    },
  });

/**
 * A `bigint` or `numeric`. The driver returns a string because neither type fits
 * a JavaScript number in general; these columns do, so the string stops here.
 */
export const createNumericTransformer = <TClient extends AnyObject, TDB extends AnyObject>(
  field: string,
  { nullable = false }: { nullable?: boolean } = {}
) =>
  createTransformer<TClient, TDB>({
    encoder: {
      sourceFields: [field] as never,
      encode: value => {
        const amount = (value as Record<string, number | null>)[field];
        if (nullable && (amount === null || amount === undefined)) {
          return { [field]: null } as never;
        }
        return { [field]: amount } as never;
      },
    },
    decoder: {
      sourceFields: [field] as never,
      decode: value => {
        const amount = (value as Record<string, string | number | null>)[field];
        if (nullable && (amount === null || amount === undefined)) {
          return { [field]: null } as never;
        }
        return { [field]: Number(amount) } as never;
      },
    },
  });
